import { TILE, PLAYER_R, WALL_MARGIN } from "../config/constants.js";

// Per-shape collision radius for blocking props (world units, before scale).
const BLOCK_R = {
  barrel: 0.34, barrel_stack: 0.45, crate: 0.45, table: 0.55, chest: 0.5, chest_gold: 0.5, chest_large: 0.55, bottle: 0.28,
  bookcase: 0.6, pillar: 0.42, bed: 0.8, gravestone: 0.42, cross: 0.34, coffin: 0.65, deadtree: 0.42,
  tree_pine: 0.5, tree_round: 0.5, tree_tall: 0.55, boulder: 0.65, boulder_large: 1.0,
  rock: 0.45, stump: 0.42, well: 0.85, cart: 0.65, haybale: 0.55, building: 1.5, lamp: 0.28,
};

/**
 * Level — the shared contract every generator produces and every system
 * consumes. It is theme-agnostic: a dungeon, a village, and a wilderness are
 * all just a walkable area plus props, spawns, an entrance and an exit.
 *
 * Two grids drive everything:
 *   floor[y][x]  = 1 if there is ground to stand/render there
 *   block[y][x]  = 1 if movement is blocked (missing floor, walls, or a
 *                  blocking prop like a tree or building footprint)
 *
 * A cell is walkable when it has floor and is not blocked. Boundary cells
 * (no floor, but next to floor) are where the theme draws its edge — dungeon
 * walls, village fences, wilderness cliffs.
 */
export class Level {
  constructor(gridW, gridH, theme) {
    this.gridW = gridW;
    this.gridH = gridH;
    this.theme = theme;
    this.floor = Array.from({ length: gridH }, () => new Uint8Array(gridW));
    this.block = Array.from({ length: gridH }, () => new Uint8Array(gridW).fill(1));
    this.variant = Array.from({ length: gridH }, () => new Uint8Array(gridW)); // floor look index
    this.elevY = Array.from({ length: gridH }, () => new Float32Array(gridW)); // world Y of each cell's floor (stories/ramps)
    this.props = [];        // { gx, gy, shape, key, color, scale, rot, blocking }
    this.blockers = [];     // { x, z, r } world-space circle collision for props (tight, not full-cell)
    this.propBlock = Array.from({ length: gridH }, () => new Uint8Array(gridW)); // pathfinding-only: cells enemies route AROUND
    this.spawnPoints = [];  // { gx, gy } enemy spawn candidates
    this.regions = [];      // { x, y, w, h, cx, cy, kind } rooms/plazas/clearings
    this.doors = [];        // { gx, gy, rot } interactable doors (blocking until opened)
    this.playerStart = { gx: gridW / 2, gy: gridH / 2 };
    this.exit = { gx: gridW / 2, gy: gridH / 2 };
  }

  /** Carve a walkable ground cell. */
  setFloor(gx, gy, variant = 0) {
    if (gx < 0 || gy < 0 || gx >= this.gridW || gy >= this.gridH) return;
    this.floor[gy][gx] = 1;
    this.block[gy][gx] = 0;
    this.variant[gy][gx] = variant;
  }

  /** Set the world Y elevation of a cell's floor. */
  setElev(gx, gy, y) {
    const x = Math.floor(gx), yy = Math.floor(gy);
    if (this.inBounds(x, yy)) this.elevY[yy][x] = y;
  }

  /** World Y of the floor beneath a world position (0 if off the map). */
  groundY(worldX, worldZ) {
    const x = Math.floor(worldX / TILE), y = Math.floor(worldZ / TILE);
    return this.inBounds(x, y) ? this.elevY[y][x] : 0;
  }

  /**
   * Add a prop. Blocking props get a TIGHT circle collider (sized to the prop),
   * not a full blocked cell — so the player/enemies can walk right up to a
   * barrel instead of stopping a whole tile away.
   */
  addProp(p) {
    this.props.push(p);
    if (p.blocking) {
      const w = this.gridToWorld(p.gx, p.gy);
      const base = BLOCK_R[p.shape] != null ? BLOCK_R[p.shape] : 0.42;
      this.blockers.push({ x: w.x, z: w.z, r: base * (p.scale || 1) });
      const gx = Math.floor(p.gx), gy = Math.floor(p.gy);   // pathfinding avoids the prop's cell
      if (this.propBlock[gy]) this.propBlock[gy][gx] = 1;
    }
  }

  /** Remove blocker circles near a world point (e.g. a barrel was smashed). */
  clearBlockerAt(worldX, worldZ, radius = 0.8) {
    this.blockers = this.blockers.filter((b) => Math.hypot(b.x - worldX, b.z - worldZ) > radius);
  }

  // ---- coordinate conversion (grid <-> world; y is up) ----
  gridToWorld(gx, gy) { return { x: gx * TILE, z: gy * TILE }; }
  worldToGrid(x, z) { return { gx: x / TILE, gy: z / TILE }; }
  playerStartWorld() { return this.gridToWorld(this.playerStart.gx + 0.5, this.playerStart.gy + 0.5); }
  exitWorld() { return this.gridToWorld(this.exit.gx + 0.5, this.exit.gy + 0.5); }

  inBounds(gx, gy) { return gx >= 0 && gy >= 0 && gx < this.gridW && gy < this.gridH; }

  hasFloorGrid(gx, gy) {
    const x = Math.floor(gx), y = Math.floor(gy);
    return this.inBounds(x, y) && this.floor[y][x] === 1;
  }

  /** Walkable = has ground and not blocked. */
  walkableGrid(gx, gy) {
    const x = Math.floor(gx), y = Math.floor(gy);
    return this.inBounds(x, y) && this.floor[y][x] === 1 && this.block[y][x] === 0;
  }

  /** Kept name-compatible with the old Dungeon API. */
  isFloorGrid(gx, gy) { return this.walkableGrid(gx, gy); }

  /**
   * World-space circle collision: the center must be walkable and a ring of
   * samples at radius r + WALL_MARGIN must all be walkable. The margin keeps
   * bodies from pressing their edges into walls or hanging off floor edges.
   */
  collidesWorld(x, z, r = PLAYER_R) {
    if (!this.walkableGrid(x / TILE, z / TILE)) return true;
    const rr = r + WALL_MARGIN;
    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * Math.PI * 2;
      if (!this.walkableGrid((x + Math.cos(a) * rr) / TILE, (z + Math.sin(a) * rr) / TILE)) return true;
    }
    // tight per-prop circle colliders
    for (const b of this.blockers) {
      const ddx = x - b.x, ddz = z - b.z, hit = r + b.r;
      if (ddx * ddx + ddz * ddz < hit * hit) return true;
    }
    return false;
  }

  /** A blocked cell that borders walkable ground → draw the themed edge here. */
  isBoundary(gx, gy) {
    if (this.floor[gy][gx] === 1) return false;
    for (let dy = -1; dy <= 1; dy++)
      for (let dx = -1; dx <= 1; dx++) {
        if (dx === 0 && dy === 0) continue;
        if (this.hasFloorGrid(gx + dx, gy + dy)) return true;
      }
    return false;
  }

  /** Flood fill from the entrance; used to guarantee the exit is reachable. */
  reachableFrom(gx, gy) {
    const seen = Array.from({ length: this.gridH }, () => new Uint8Array(this.gridW));
    const stack = [[Math.floor(gx), Math.floor(gy)]];
    let count = 0;
    while (stack.length) {
      const [x, y] = stack.pop();
      if (!this.inBounds(x, y) || seen[y][x]) continue;
      if (this.floor[y][x] !== 1 || this.block[y][x] !== 0) continue;
      seen[y][x] = 1; count++;
      stack.push([x + 1, y], [x - 1, y], [x, y + 1], [x, y - 1]);
    }
    return { seen, count };
  }

  /**
   * BFS "flow field": distance (in steps) from the target cell to every
   * reachable walkable cell. Enemies follow the descending gradient to path
   * AROUND walls toward the player. Cheap — only rebuilt when the player
   * changes cells.
   */
  buildFlow(worldX, worldZ) {
    const W = this.gridW, H = this.gridH;
    const dist = Array.from({ length: H }, () => new Int16Array(W).fill(-1));
    const g = this.worldToGrid(worldX, worldZ);
    const sx = Math.floor(g.gx), sy = Math.floor(g.gy);
    this.flow = dist;
    if (!this.walkableGrid(sx, sy)) return;
    const q = [[sx, sy]]; dist[sy][sx] = 0; let head = 0;
    while (head < q.length) {
      const [x, y] = q[head++]; const d = dist[y][x];
      for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
        const nx = x + dx, ny = y + dy;
        if (nx < 0 || ny < 0 || nx >= W || ny >= H || dist[ny][nx] !== -1) continue;
        if (this.floor[ny][nx] !== 1 || this.block[ny][nx] !== 0) continue;
        dist[ny][nx] = d + 1; q.push([nx, ny]);
      }
    }
  }

  /** World-space direction toward the player along the flow field, or null. */
  flowDirWorld(worldX, worldZ) {
    if (!this.flow) return null;
    const g = this.worldToGrid(worldX, worldZ);
    const x = Math.floor(g.gx), y = Math.floor(g.gy);
    if (!this.inBounds(x, y) || this.flow[y][x] < 0) return null;
    let best = this.flow[y][x], bx = x, by = y;
    for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [1, -1], [-1, 1], [-1, -1]]) {
      const nx = x + dx, ny = y + dy;
      if (!this.inBounds(nx, ny) || this.flow[ny][nx] < 0) continue;
      if (dx !== 0 && dy !== 0 && (this.flow[y][nx] < 0 || this.flow[ny][x] < 0)) continue; // no corner cut
      if (this.flow[ny][nx] < best) { best = this.flow[ny][nx]; bx = nx; by = ny; }
    }
    if (bx === x && by === y) return null;
    const dirx = (bx + 0.5) - g.gx, dirz = (by + 0.5) - g.gy;
    const len = Math.hypot(dirx, dirz) || 1;
    return { x: dirx / len, z: dirz / len };
  }
}
